import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useLanguage, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import PageHeader from "../components/layout/PageHeader";
import HeatmapCalendar from "../components/analytics/HeatmapCalendar";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Calendar, Download, Target,
  Award, Flame, Activity, BarChart3, Loader2
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

const COLORS = ["#0D9488", "#F59E0B", "#8B5CF6", "#EC4899", "#10B981"];

const Analytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";

  const [isLoading, setIsLoading] = useState(true);
  const [habits, setHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [gamification, setGamification] = useState(null);
  const [timeRange, setTimeRange] = useState("30"); // days
  const [selectedHabit, setSelectedHabit] = useState("all");

  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Fetch habits
      const habitsRes = await apiClient.get("/habits");
      const habitsData = habitsRes.data?.habits || habitsRes.data || [];
      const enabledHabits = Array.isArray(habitsData) ? habitsData.filter((h) => h.enabled) : [];
      setHabits(enabledHabits);

      // Fetch habit logs for the last 365 days - wrap in try-catch in case endpoint doesn't exist
      try {
        const logsRes = await apiClient.get("/habit-logs", {
          params: { start_date: getDateXDaysAgo(365), end_date: getTodayDate() },
        });
        setHabitLogs(logsRes.data.logs || []);
      } catch (logsError) {
        console.warn("Habit logs endpoint not available, using empty array");
        setHabitLogs([]);
      }

      // Fetch gamification data - wrap in try-catch
      try {
        const gamificationRes = await apiClient.get("/gamification/me");
        setGamification(gamificationRes.data.gamification);
      } catch (gameError) {
        console.warn("Gamification endpoint not available");
        setGamification(null);
      }
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      toast.error(isEnglish ? "Failed to load analytics" : "Gagal memuat analitik");
    } finally {
      setIsLoading(false);
    }
  };

  const getDateXDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return getLocalDate(date);
  };

  const getTodayDate = () => {
    return getLocalDate();
  };

  // Filter logs based on time range
  const filteredLogs = React.useMemo(() => {
    if (!habitLogs.length) return [];

    const cutoffDate = getDateXDaysAgo(parseInt(timeRange));
    return habitLogs.filter((log) => log.date >= cutoffDate);
  }, [habitLogs, timeRange]);

  // Filter logs by selected habit
  const habitFilteredLogs = React.useMemo(() => {
    if (selectedHabit === "all") return filteredLogs;
    return filteredLogs.filter((log) => log.habit_id === selectedHabit);
  }, [filteredLogs, selectedHabit]);

  // Generate heatmap data
  const heatmapData = React.useMemo(() => {
    const logsByDate = {};
    habitLogs.forEach((log) => {
      if (!logsByDate[log.date]) {
        logsByDate[log.date] = { date: log.date, count: 0 };
      }
      logsByDate[log.date].count += 1;
    });
    return Object.values(logsByDate);
  }, [habitLogs]);

  // Generate daily completion data for line chart
  const dailyCompletionData = React.useMemo(() => {
    const data = [];
    const days = parseInt(timeRange);

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = getLocalDate(date);

      const dayLogs = habitFilteredLogs.filter((log) => log.date === dateStr);
      const uniqueHabits = new Set(dayLogs.map((log) => log.habit_id));

      data.push({
        date: dateStr,
        completed: uniqueHabits.size,
        target: habits.length || 1,
      });
    }

    return data;
  }, [habitFilteredLogs, habits.length, timeRange]);

  // Generate category distribution data
  const categoryDistribution = React.useMemo(() => {
    const categoryCount = {};
    const habitCategories = {};

    habits.forEach((h) => {
      habitCategories[h.habit_id] = h.category;
    });

    habitFilteredLogs.forEach((log) => {
      const category = habitCategories[log.habit_id] || "general";
      categoryCount[category] = (categoryCount[category] || 0) + 1;
    });

    return Object.entries(categoryCount).map(([name, value]) => {
      const categoryNames = {
        quran: isEnglish ? "Quran" : "Quran",
        shalat: isEnglish ? "Prayer" : "Shalat",
        infaq: isEnglish ? "Charity" : "Infaq",
        general: isEnglish ? "General" : "Umum",
      };
      return { name: categoryNames[name] || name, value };
    });
  }, [habitFilteredLogs, habits, isEnglish]);

  // Calculate performance insights
  const performanceInsights = React.useMemo(() => {
    if (!dailyCompletionData.length) return [];

    const insights = [];
    const recentDays = dailyCompletionData.slice(-7);
    const avgCompletion =
      recentDays.reduce((sum, day) => sum + day.completed, 0) / recentDays.length;
    const prevDays = dailyCompletionData.slice(-14, -7);
    const prevAvgCompletion =
      prevDays.reduce((sum, day) => sum + day.completed, 0) / prevDays.length;

    // Trend
    if (avgCompletion > prevAvgCompletion * 1.1) {
      insights.push({
        type: "positive",
        icon: TrendingUp,
        title: isEnglish ? "Improving!" : "Meningkat!",
        message: isEnglish
          ? `Your completion rate increased by ${(((avgCompletion - prevAvgCompletion) / prevAvgCompletion) * 100).toFixed(0)}% this week`
          : `Tingkat penyelesaian Anda meningkat ${(((avgCompletion - prevAvgCompletion) / prevAvgCompletion) * 100).toFixed(0)}% minggu ini`,
      });
    } else if (avgCompletion < prevAvgCompletion * 0.9) {
      insights.push({
        type: "warning",
        icon: TrendingDown,
        title: isEnglish ? "Needs attention" : "Perlu perhatian",
        message: isEnglish
          ? `Your completion rate decreased this week. Keep going!`
          : `Tingkat penyelesaian menurun minggu ini. Terus semangat!`,
      });
    }

    // Streak
    if (gamification?.overall_streak > 0) {
      insights.push({
        type: "positive",
        icon: Flame,
        title: isEnglish ? "On fire!" : "Pedas!",
        message: isEnglish
          ? `You're on a ${gamification.overall_streak}-day streak!`
          : `Anda sedang berstreak ${gamification.overall_streak} hari!`,
      });
    }

    // Best day
    const bestDay = dailyCompletionData.reduce((best, day) =>
      day.completed > best.completed ? day : best
    );
    if (bestDay.completed > 0) {
      insights.push({
        type: "info",
        icon: Award,
        title: isEnglish ? "Best performance" : "Performa terbaik",
        message: isEnglish
          ? `Your best day was ${new Date(bestDay.date).toLocaleDateString(isEnglish ? "en-US" : "id-ID", { month: "short", day: "numeric" })} with ${bestDay.completed} habits completed`
          : `Hari terbaik Anda adalah ${new Date(bestDay.date).toLocaleDateString(isEnglish ? "en-US" : "id-ID", { month: "short", day: "numeric" })} dengan ${bestDay.completed} kebiasaan selesai`,
      });
    }

    const weekdayNamesEn = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const weekdayNamesId = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
    const weekdayStats = {};
    habitFilteredLogs.forEach((log) => {
      const d = new Date(log.date);
      const w = d.getDay();
      weekdayStats[w] = (weekdayStats[w] || 0) + 1;
    });
    const sortedWeekdays = Object.entries(weekdayStats).sort((a, b) => b[1] - a[1]);
    if (sortedWeekdays.length > 0) {
      const [topW, topCount] = sortedWeekdays[0];
      const name = isEnglish ? weekdayNamesEn[topW] : weekdayNamesId[topW];
      insights.push({
        type: "info",
        icon: Calendar,
        title: isEnglish ? "Best day for worship" : "Hari terbaik untuk ibadah",
        message: isEnglish
          ? `Your most productive day is ${name}. Plan key habits on this day.`
          : `Hari paling produktif Anda adalah ${name}. Jadwalkan ibadah utama pada hari ini.`,
      });
      if (sortedWeekdays.length > 1) {
        const tail = sortedWeekdays.slice(-2).map(([w]) => (isEnglish ? weekdayNamesEn[w] : weekdayNamesId[w])).join(", ");
        insights.push({
          type: "warning",
          icon: Activity,
          title: isEnglish ? "Productivity pattern" : "Pola produktivitas",
          message: isEnglish
            ? `Lower activity on ${tail}. Try shorter sessions or reminders.`
            : `Aktivitas lebih rendah di ${tail}. Coba sesi singkat atau pengingat.`,
        });
      }
    }

    const targets = {};
    habits.forEach((h) => {
      targets[h.habit_id] = h.target_per_day || 1;
    });
    const completions = {};
    habitFilteredLogs.forEach((log) => {
      completions[log.habit_id] = (completions[log.habit_id] || 0) + 1;
    });
    const weakness = Object.entries(completions)
      .map(([hid, count]) => {
        const h = habits.find((x) => x.habit_id === hid);
        const name = h?.name || hid;
        const t = targets[hid] || 1;
        const days = parseInt(timeRange);
        const ratio = count / Math.max(1, days * t);
        return { hid, name, ratio };
      })
      .filter((x) => x.ratio < 0.5)
      .sort((a, b) => a.ratio - b.ratio)
      .slice(0, 3);
    if (weakness.length > 0) {
      const names = weakness.map((w) => w.name).join(", ");
      insights.push({
        type: "warning",
        icon: Target,
        title: isEnglish ? "Weakness detected" : "Kelemahan terdeteksi",
        message: isEnglish
          ? `Focus on ${names}. Reduce targets and add reminders.`
          : `Fokus pada ${names}. Kurangi target dan tambahkan pengingat.`,
      });
    }

    return insights;
  }, [dailyCompletionData, gamification, isEnglish]);

  // Export data
  const handleExport = async (format) => {
    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        timeRange: `${timeRange} days`,
        habits: habits.map((h) => ({
          name: h.name,
          category: h.category,
          targetPerDay: h.target_per_day,
        })),
        dailyCompletion: dailyCompletionData,
        categoryDistribution: categoryDistribution,
        gamification: gamification,
      };

      if (format === "json") {
        // Download as JSON
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `quran-companion-analytics-${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === "csv") {
        // Download as CSV
        const headers = ["Date", "Completed Habits", "Target Habits"];
        const rows = dailyCompletionData.map((day) => [day.date, day.completed, day.target]);
        const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `quran-companion-analytics-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === "pdf") {
        const rangeLabel = `${timeRange} ${isEnglish ? "days" : "hari"}`;
        const categoryRows = categoryDistribution
          .map((cat) => `<tr><td>${cat.name}</td><td style="text-align:right;">${cat.value}</td></tr>`)
          .join("");
        const completionRows = dailyCompletionData
          .map((day) => `<tr><td>${day.date}</td><td style="text-align:right;">${day.completed}</td><td style="text-align:right;">${day.target}</td></tr>`)
          .join("");

        const html = `
          <html>
            <head>
              <title>Analytics Export</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
                h1 { font-size: 20px; margin-bottom: 8px; }
                h2 { font-size: 16px; margin: 20px 0 8px; }
                table { width: 100%; border-collapse: collapse; margin-top: 8px; }
                th, td { border: 1px solid #E5E7EB; padding: 6px 8px; font-size: 12px; }
                th { background: #F3F4F6; text-align: left; }
                .meta { font-size: 12px; color: #6B7280; }
              </style>
            </head>
            <body>
              <h1>${isEnglish ? "Analytics Export" : "Ekspor Analitik"}</h1>
              <div class="meta">
                <div>${isEnglish ? "User" : "Pengguna"}: ${user?.name || "-"}</div>
                <div>${isEnglish ? "Range" : "Rentang"}: ${rangeLabel}</div>
                <div>${isEnglish ? "Export Date" : "Tanggal Ekspor"}: ${new Date().toLocaleString()}</div>
              </div>
              <h2>${isEnglish ? "Category Distribution" : "Distribusi Kategori"}</h2>
              <table>
                <thead>
                  <tr>
                    <th>${isEnglish ? "Category" : "Kategori"}</th>
                    <th style="text-align:right;">${isEnglish ? "Total" : "Total"}</th>
                  </tr>
                </thead>
                <tbody>
                  ${categoryRows || `<tr><td colspan="2">${isEnglish ? "No data" : "Tidak ada data"}</td></tr>`}
                </tbody>
              </table>
              <h2>${isEnglish ? "Daily Completion" : "Penyelesaian Harian"}</h2>
              <table>
                <thead>
                  <tr>
                    <th>${isEnglish ? "Date" : "Tanggal"}</th>
                    <th style="text-align:right;">${isEnglish ? "Completed" : "Selesai"}</th>
                    <th style="text-align:right;">${isEnglish ? "Target" : "Target"}</th>
                  </tr>
                </thead>
                <tbody>
                  ${completionRows || `<tr><td colspan="3">${isEnglish ? "No data" : "Tidak ada data"}</td></tr>`}
                </tbody>
              </table>
            </body>
          </html>
        `;

        const printWindow = window.open("", "_blank", "noopener,noreferrer");
        if (!printWindow) {
          toast.error(isEnglish ? "Popup blocked" : "Popup diblokir");
          return;
        }
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }

      toast.success(isEnglish ? `Exported as ${format.toUpperCase()}` : `Diekspor sebagai ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error(isEnglish ? "Failed to export data" : "Gagal mengekspor data");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="analytics-page">
      <PageHeader title={isEnglish ? "Analytics" : "Analitik"} />
      <div className="page-container space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">{isEnglish ? "Last 7 days" : "7 hari terakhir"}</SelectItem>
                    <SelectItem value="30">{isEnglish ? "Last 30 days" : "30 hari terakhir"}</SelectItem>
                    <SelectItem value="90">{isEnglish ? "Last 90 days" : "90 hari terakhir"}</SelectItem>
                    <SelectItem value="365">{isEnglish ? "Last year" : "Tahun terakhir"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <Select value={selectedHabit} onValueChange={setSelectedHabit}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isEnglish ? "All Habits" : "Semua Kebiasaan"}</SelectItem>
                    {habits.map((habit) => (
                      <SelectItem key={habit.habit_id} value={habit.habit_id}>
                        {habit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExport("csv")}>
                  <Download className="w-4 h-4 mr-1" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExport("json")}>
                  <Download className="w-4 h-4 mr-1" />
                  JSON
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExport("pdf")}>
                  <Download className="w-4 h-4 mr-1" />
                  PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Insights */}
        {performanceInsights.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {performanceInsights.map((insight, index) => {
              const Icon = insight.icon;
              return (
                <Card key={index} className={`border-l-4 ${
                  insight.type === "positive" ? "border-l-green-500" :
                  insight.type === "warning" ? "border-l-orange-500" :
                  "border-l-blue-500"
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        insight.type === "positive" ? "bg-green-500/10 text-green-600" :
                        insight.type === "warning" ? "bg-orange-500/10 text-orange-600" :
                        "bg-blue-500/10 text-blue-600"
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{insight.title}</h4>
                        <p className="text-sm text-muted-foreground">{insight.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Heatmap Calendar */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                {isEnglish ? "Activity Heatmap" : "Heatmap Aktivitas"}
              </h3>
              <Badge variant="secondary">{isEnglish ? "This Year" : "Tahun Ini"}</Badge>
            </div>
            <HeatmapCalendar data={heatmapData} language={language} />
          </CardContent>
        </Card>

        {/* Charts - Stacked Layout */}
        <div className="space-y-6">
          {/* Daily Completion Trend - Full Width */}
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-4">{isEnglish ? "Daily Completion Trend" : "Tren Penyelesaian Harian"}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyCompletionData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString(isEnglish ? "en-US" : "id-ID", { month: "short", day: "numeric" })}
                    className="text-xs"
                  />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--background))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    labelFormatter={(date) => new Date(date).toLocaleDateString(isEnglish ? "en-US" : "id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    name={isEnglish ? "Completed" : "Selesai"}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Category Distribution - Full Width Below */}
          <Card>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm sm:text-base">
                  {isEnglish ? "Category Distribution" : "Distribusi Kategori"}
                </h3>
                <Badge variant="outline" className="text-xs sm:text-sm">
                  {categoryDistribution.reduce((sum, cat) => sum + cat.value, 0)} {isEnglish ? "total" : "total"}
                </Badge>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                {categoryDistribution.slice(0, 2).map((cat, idx) => {
                  const percent = ((cat.value / categoryDistribution.reduce((sum, c) => sum + c.value, 0)) * 100).toFixed(1);
                  return (
                    <div key={idx} className="bg-muted/50 rounded-lg p-2 sm:p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div
                          className="w-3 h-3 sm:w-4 sm:h-4 rounded"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        />
                        <p className="text-xs sm:text-sm font-medium truncate">{cat.name}</p>
                      </div>
                      <p className="text-lg sm:text-xl font-bold">{percent}%</p>
                    </div>
                  );
                })}
              </div>

              {/* Pie Chart with Legend */}
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <ResponsiveContainer width="100%" height={220} className="sm:w-1/2">
                  <PieChart>
                    <Pie
                      data={categoryDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                      fontSize={12}
                      fontWeight={600}
                    >
                      {categoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => {
                        const percent = ((value / categoryDistribution.reduce((sum, c) => sum + c.value, 0)) * 100).toFixed(1);
                        return [`${value} (${percent}%)`, name];
                      }}
                      contentStyle={{
                        backgroundColor: "hsl(var(--background))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>

                {/* Custom Legend */}
                <div className="flex-1 w-full sm:w-1/2">
                  <div className="space-y-2 sm:space-y-3">
                    {categoryDistribution.map((cat, idx) => {
                      const percent = ((cat.value / categoryDistribution.reduce((sum, c) => sum + c.value, 0)) * 100).toFixed(1);
                      return (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div
                              className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0"
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <p className="text-xs sm:text-sm font-medium truncate">{cat.name}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs sm:text-sm font-bold">{percent}%</p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">({cat.value})</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Best Performance Days Analysis */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              {isEnglish ? "Best Performance Days" : "Hari Performa Terbaik"}
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {isEnglish ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] : ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"]
                .map((dayName, index) => {
                  const dayLogs = habitFilteredLogs.filter((log) => {
                    const dayOfWeek = new Date(log.date).getDay();
                    return dayOfWeek === index;
                  });
                  const avgPerDay = dayLogs.length / (parseInt(timeRange) / 7);

                  return (
                    <div key={index} className="text-center">
                      <div className="text-xs text-muted-foreground mb-2">{dayName}</div>
                      <div
                        className="h-20 rounded-lg flex items-end justify-center p-2 relative"
                        style={{
                          background: `linear-gradient(to top, hsl(var(--primary)) ${Math.min((avgPerDay / (habits.length || 1)) * 100, 100)}%, hsl(var(--muted)) 0%)`,
                        }}
                      >
                        <span className="absolute bottom-1 text-xs font-bold text-primary-foreground">
                          {avgPerDay.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
